import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/authContext';
import { getDocument } from '../utils/firestoreService';
import { resolveFeatureFlag, isFeatureToggleVisible } from '../utils/featureFlags';
import { FEATURE_FLAG_DEFINITIONS, userToggleFlags, meetsMinRole } from '../config/featureFlagDefinitions';
import { getAuthHeaders } from '../utils/apiUtils';
import { ServerURI } from '../globals';

const FeatureFlagContext = createContext({});

export const FeatureFlagProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [resolvedFlags, setResolvedFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [chatAvailable, setChatAvailable] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    let disposed = false;
    let inFlight = false;
    let retryAt = 0;
    const refresh = async () => {
      if (disposed || inFlight || Date.now() < retryAt) return;
      inFlight = true;
      try {
        const response = await fetch(`${ServerURI}/api/v3/chat/status`, { headers: await getAuthHeaders() });
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const seconds = Number(retryAfter);
          const retryDate = Date.parse(retryAfter);
          // Both the interval and visibility-triggered checks honor server backoff.
          retryAt = seconds > 0 && Number.isFinite(seconds)
            ? Date.now() + seconds * 1000
            : retryDate > Date.now() ? retryDate : Date.now() + 60000;
          return;
        }
        if (response.ok) {
          const status = await response.json();
          if (!disposed) setChatAvailable(status.enabled === true);
        }
      } catch { /* Keep last known UI status; the backend always enforces the stop. */ }
      finally { inFlight = false; }
    };
    refresh();
    const interval = setInterval(refresh, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setResolvedFlags({});
      setIsLoading(false);
      return;
    }
    const userId = currentUser.uid;
    let disposed = false;
    let resolution = 0;
    setIsLoading(true);

    // Resolve flags from a user doc snapshot. Subscribing to users/{uid}
    // closes a signup race where currentUser becomes truthy before
    // setDoc(users/{uid}, { settings }) completes. With onSnapshot, the post-
    // signup write triggers a re-resolve automatically. It also lets admin
    // toggles on the user doc propagate without a reload.
    const userRef = doc(db, 'users', currentUser.uid);

    const resolveFromUserDoc = async (userDoc) => {
      const request = ++resolution;
      try {
        const userSettings = userDoc?.settings || {};
        const systemConfig = await getDocument('globalSettings', 'systemConfig');
        const companyDoc = userDoc?.companyId
          ? await getDocument('companies', userDoc.companyId)
          : null;
        const userRole = userDoc?.userRole || 'standard';

        // Read per-company minRole overrides (falls back to definition defaults)
        const flagMinRoles = companyDoc?.flagMinRoles || {};

        const flags = {};
        for (const flag of FEATURE_FLAG_DEFINITIONS) {
          const effectiveMinRole = flagMinRoles[flag.key] !== undefined
            ? flagMinRoles[flag.key]
            : flag.minRole;

          if (flag.systemOnly) {
            // System-level only — no company or user override
            flags[flag.key] = systemConfig?.featureFlags?.[flag.key] === true;
          } else if (flag.userSettingKey) {
            // Every personal preference requires company permission. Only
            // features defined with a system gate require a system switch.
            const { enabled } = resolveFeatureFlag(flag.key, {
              systemConfig,
              companyDoc,
              userSettings,
              userSettingKey: flag.userSettingKey,
              userRole,
              minRole: effectiveMinRole,
              hasSystemGate: flag.hasSystemGate,
            });
            flags[flag.key] = enabled;
          } else if (flag.hasSystemGate) {
            // System + company gate only (no user setting)
            const systemOn = systemConfig?.featureFlags?.[flag.key] === true;
            const companyOn = companyDoc?.featureFlags?.[flag.key] !== false;
            flags[flag.key] = systemOn && companyOn && meetsMinRole(userRole, effectiveMinRole);
          } else {
            // Company-only gate (no system gate, no user toggle)
            const companyOn = companyDoc?.featureFlags?.[flag.key] !== false;
            flags[flag.key] = companyOn && meetsMinRole(userRole, effectiveMinRole);
          }
        }

        // Visible flags are only the user-toggle flags that pass gate + role checks
        const visibleFlags = userToggleFlags.filter((flag) => {
          const effectiveMinRole = flagMinRoles[flag.key] !== undefined
            ? flagMinRoles[flag.key]
            : flag.minRole;
          if (!meetsMinRole(userRole, effectiveMinRole)) return false;
          return isFeatureToggleVisible(flag.key, { systemConfig, companyDoc, hasSystemGate: flag.hasSystemGate });
        });

        if (disposed || request !== resolution) return;
        setResolvedFlags({ userId, flags, visibleFlags, userRole, flagMinRoles });
      } catch (error) {
        if (disposed || request !== resolution) return;
        console.error('Error resolving feature flags:', error);
        setResolvedFlags({ userId });
      }
      setIsLoading(false);
    };

    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        // If the user doc doesn't exist yet (signup race window), keep
        // isLoading=true and wait for the next snapshot. The snapshot
        // listener will fire again as soon as Signup.js writes the doc.
        if (!snapshot.exists()) {
          resolution++;
          setResolvedFlags({});
          setIsLoading(true);
          return;
        }
        resolveFromUserDoc(snapshot.data());
      },
      (error) => {
        resolution++;
        console.error('FeatureFlagContext snapshot error:', error);
        setResolvedFlags({ userId });
        setIsLoading(false);
      }
    );

    return () => { disposed = true; unsubscribe(); };
  }, [currentUser?.uid]);

  // Never expose the previous account's values during the render before the
  // new subscription resolves, or accept a late response from that account.
  const isCurrentUser = !!currentUser?.uid && resolvedFlags.userId === currentUser.uid;
  const effective = isCurrentUser ? resolvedFlags : {};
  return (
    <FeatureFlagContext.Provider value={{ ...effective,
      isLoading: !!currentUser?.uid && (isLoading || !isCurrentUser),
      flags: { ...effective.flags, aiAssistant: !!effective.flags?.aiAssistant && chatAvailable } }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);

